// Permission types for the application

export type PermissionKey =
  // Dashboard
  | "dashboard.view"
  
  // Inventory
  | "inventory.stock-items.view"
  | "inventory.stock-items.create"
  | "inventory.stock-items.edit"
  | "inventory.stock-items.delete"
  | "inventory.warehouses.view"
  | "inventory.warehouses.create"
  | "inventory.warehouses.edit"
  | "inventory.warehouses.delete"
  | "inventory.movements.view"
  | "inventory.movements.create"
  | "inventory.movements.edit"
  | "inventory.movements.delete"
  | "inventory.stock-takes.view"
  | "inventory.stock-takes.create"
  | "inventory.stock-takes.edit"
  | "inventory.stock-takes.delete"
  | "inventory.tracking.view"
  | "inventory.tracking.edit"
  
  // Purchasing
  | "purchasing.orders.view"
  | "purchasing.orders.create"
  | "purchasing.orders.edit"
  | "purchasing.orders.delete"
  | "purchasing.orders.approve"
  | "purchasing.receive-goods.view"
  | "purchasing.receive-goods.create"
  | "purchasing.suppliers.view"
  | "purchasing.suppliers.create"
  | "purchasing.suppliers.edit"
  | "purchasing.suppliers.delete"
  
  // Sales
  | "sales.orders.view"
  | "sales.orders.create"
  | "sales.orders.edit"
  | "sales.orders.delete"
  | "sales.orders.approve"
  | "sales.pick-pack.view"
  | "sales.pick-pack.create"
  | "sales.shipping.view"
  | "sales.shipping.create"
  | "sales.customers.view"
  | "sales.customers.create"
  | "sales.customers.edit"
  | "sales.customers.delete"
  
  // Master Data
  | "master-data.projects.view"
  | "master-data.projects.create"
  | "master-data.projects.edit"
  | "master-data.projects.delete"
  
  // Manufacturing
  | "manufacturing.boms.view"
  | "manufacturing.boms.create"
  | "manufacturing.boms.edit"
  | "manufacturing.boms.delete"
  | "manufacturing.work-orders.view"
  | "manufacturing.work-orders.create"
  | "manufacturing.work-orders.edit"
  | "manufacturing.work-orders.delete"
  | "manufacturing.production-schedule.view"
  | "manufacturing.production-schedule.edit"
  | "manufacturing.routing.view"
  | "manufacturing.routing.edit"
  
  // AI & Analytics
  | "ai.forecasting.view"
  | "ai.abc-classification.view"
  | "ai.reorder.view"
  | "ai.reorder.approve"
  
  // Pricing
  | "pricing.matrix.view"
  | "pricing.matrix.edit"
  | "pricing.margin-analysis.view"
  | "pricing.cost-analysis.view"
  
  // Settings
  | "settings.tax-rates.view"
  | "settings.tax-rates.create"
  | "settings.tax-rates.edit"
  | "settings.tax-rates.delete"
  | "settings.custom-fields.view"
  | "settings.custom-fields.create"
  | "settings.custom-fields.edit"
  | "settings.custom-fields.delete"
  | "settings.integration.view"
  | "settings.integration.edit"
  | "settings.sync-status.view"
  | "settings.stock-options.view"
  | "settings.stock-options.edit"
  | "settings.company-details.view"
  | "settings.user-profiles.view"
  | "settings.user-profiles.create"
  | "settings.user-profiles.edit"
  | "settings.user-profiles.delete"
  | "settings.users.view"
  | "settings.users.create"
  | "settings.users.edit"
  | "settings.users.delete";

export interface Permission {
  key: PermissionKey;
  label: string;
  description: string;
  category: string;
}

export interface UserProfile {
  id: string;
  name: string;
  description: string;
  permissions: PermissionKey[];
  isDefault: boolean;
  isSystem: boolean; // System profiles cannot be deleted
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  profileId: string;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
}

export const PERMISSION_CATEGORIES = [
  { id: "dashboard", label: "Dashboard" },
  { id: "inventory", label: "Inventory" },
  { id: "purchasing", label: "Purchasing" },
  { id: "sales", label: "Sales & Fulfillment" },
  { id: "master-data", label: "Master Data" },
  { id: "manufacturing", label: "Manufacturing" },
  { id: "ai", label: "AI & Analytics" },
  { id: "pricing", label: "Pricing" },
  { id: "settings", label: "Settings" },
] as const;

export const ALL_PERMISSIONS: Permission[] = [
  // Dashboard
  { key: "dashboard.view", label: "View Dashboard", description: "Access to main dashboard", category: "dashboard" },
  
  // Inventory
  { key: "inventory.stock-items.view", label: "View Stock Items", description: "View stock items list", category: "inventory" },
  { key: "inventory.stock-items.create", label: "Create Stock Items", description: "Create new stock items", category: "inventory" },
  { key: "inventory.stock-items.edit", label: "Edit Stock Items", description: "Edit existing stock items", category: "inventory" },
  { key: "inventory.stock-items.delete", label: "Delete Stock Items", description: "Delete stock items", category: "inventory" },
  { key: "inventory.warehouses.view", label: "View Warehouses", description: "View warehouses and locations", category: "inventory" },
  { key: "inventory.warehouses.create", label: "Create Warehouses", description: "Create new warehouses/locations", category: "inventory" },
  { key: "inventory.warehouses.edit", label: "Edit Warehouses", description: "Edit warehouses/locations", category: "inventory" },
  { key: "inventory.warehouses.delete", label: "Delete Warehouses", description: "Delete warehouses/locations", category: "inventory" },
  { key: "inventory.movements.view", label: "View Stock Movements", description: "View stock movement history", category: "inventory" },
  { key: "inventory.movements.create", label: "Create Stock Movements", description: "Create stock movements", category: "inventory" },
  { key: "inventory.movements.edit", label: "Edit Stock Movements", description: "Edit stock movements", category: "inventory" },
  { key: "inventory.movements.delete", label: "Delete Stock Movements", description: "Delete stock movements", category: "inventory" },
  { key: "inventory.stock-takes.view", label: "View Stock Takes", description: "View stock takes", category: "inventory" },
  { key: "inventory.stock-takes.create", label: "Create Stock Takes", description: "Create new stock takes", category: "inventory" },
  { key: "inventory.stock-takes.edit", label: "Edit Stock Takes", description: "Edit stock takes", category: "inventory" },
  { key: "inventory.stock-takes.delete", label: "Delete Stock Takes", description: "Delete stock takes", category: "inventory" },
  { key: "inventory.tracking.view", label: "View Lot/Batch/Serial", description: "View tracking information", category: "inventory" },
  { key: "inventory.tracking.edit", label: "Edit Lot/Batch/Serial", description: "Edit tracking information", category: "inventory" },
  
  // Purchasing
  { key: "purchasing.orders.view", label: "View Purchase Orders", description: "View purchase orders", category: "purchasing" },
  { key: "purchasing.orders.create", label: "Create Purchase Orders", description: "Create new purchase orders", category: "purchasing" },
  { key: "purchasing.orders.edit", label: "Edit Purchase Orders", description: "Edit purchase orders", category: "purchasing" },
  { key: "purchasing.orders.delete", label: "Delete Purchase Orders", description: "Delete purchase orders", category: "purchasing" },
  { key: "purchasing.orders.approve", label: "Approve Purchase Orders", description: "Approve purchase orders", category: "purchasing" },
  { key: "purchasing.receive-goods.view", label: "View Goods Receipt", description: "View goods receipt", category: "purchasing" },
  { key: "purchasing.receive-goods.create", label: "Create Goods Receipt", description: "Create goods receipt", category: "purchasing" },
  { key: "purchasing.suppliers.view", label: "View Suppliers", description: "View suppliers list", category: "purchasing" },
  { key: "purchasing.suppliers.create", label: "Create Suppliers", description: "Create new suppliers", category: "purchasing" },
  { key: "purchasing.suppliers.edit", label: "Edit Suppliers", description: "Edit suppliers", category: "purchasing" },
  { key: "purchasing.suppliers.delete", label: "Delete Suppliers", description: "Delete suppliers", category: "purchasing" },
  
  // Sales
  { key: "sales.orders.view", label: "View Sales Orders", description: "View sales orders", category: "sales" },
  { key: "sales.orders.create", label: "Create Sales Orders", description: "Create new sales orders", category: "sales" },
  { key: "sales.orders.edit", label: "Edit Sales Orders", description: "Edit sales orders", category: "sales" },
  { key: "sales.orders.delete", label: "Delete Sales Orders", description: "Delete sales orders", category: "sales" },
  { key: "sales.orders.approve", label: "Approve Sales Orders", description: "Approve sales orders", category: "sales" },
  { key: "sales.pick-pack.view", label: "View Pick & Pack", description: "View pick & pack operations", category: "sales" },
  { key: "sales.pick-pack.create", label: "Create Pick & Pack", description: "Create pick & pack operations", category: "sales" },
  { key: "sales.shipping.view", label: "View Shipping", description: "View shipping information", category: "sales" },
  { key: "sales.shipping.create", label: "Create Shipping", description: "Create shipping records", category: "sales" },
  { key: "sales.customers.view", label: "View Customers", description: "View customers list", category: "sales" },
  { key: "sales.customers.create", label: "Create Customers", description: "Create new customers", category: "sales" },
  { key: "sales.customers.edit", label: "Edit Customers", description: "Edit customers", category: "sales" },
  { key: "sales.customers.delete", label: "Delete Customers", description: "Delete customers", category: "sales" },
  
  // Master Data
  { key: "master-data.projects.view", label: "View Projects & Cost Centers", description: "View projects and cost centers", category: "master-data" },
  { key: "master-data.projects.create", label: "Create Projects & Cost Centers", description: "Create new projects and cost centers", category: "master-data" },
  { key: "master-data.projects.edit", label: "Edit Projects & Cost Centers", description: "Edit projects and cost centers", category: "master-data" },
  { key: "master-data.projects.delete", label: "Delete Projects & Cost Centers", description: "Delete projects and cost centers", category: "master-data" },
  
  // Manufacturing
  { key: "manufacturing.boms.view", label: "View BOMs", description: "View bills of material", category: "manufacturing" },
  { key: "manufacturing.boms.create", label: "Create BOMs", description: "Create new BOMs", category: "manufacturing" },
  { key: "manufacturing.boms.edit", label: "Edit BOMs", description: "Edit BOMs", category: "manufacturing" },
  { key: "manufacturing.boms.delete", label: "Delete BOMs", description: "Delete BOMs", category: "manufacturing" },
  { key: "manufacturing.work-orders.view", label: "View Work Orders", description: "View work orders", category: "manufacturing" },
  { key: "manufacturing.work-orders.create", label: "Create Work Orders", description: "Create new work orders", category: "manufacturing" },
  { key: "manufacturing.work-orders.edit", label: "Edit Work Orders", description: "Edit work orders", category: "manufacturing" },
  { key: "manufacturing.work-orders.delete", label: "Delete Work Orders", description: "Delete work orders", category: "manufacturing" },
  { key: "manufacturing.production-schedule.view", label: "View Production Schedule", description: "View production schedule", category: "manufacturing" },
  { key: "manufacturing.production-schedule.edit", label: "Edit Production Schedule", description: "Edit production schedule", category: "manufacturing" },
  { key: "manufacturing.routing.view", label: "View Routings", description: "View routings & work centers", category: "manufacturing" },
  { key: "manufacturing.routing.edit", label: "Edit Routings", description: "Edit routings & work centers", category: "manufacturing" },
  
  // AI & Analytics
  { key: "ai.forecasting.view", label: "View Demand Forecasting", description: "View AI demand forecasting", category: "ai" },
  { key: "ai.abc-classification.view", label: "View ABC Classification", description: "View ABC classification", category: "ai" },
  { key: "ai.reorder.view", label: "View Reorder Recommendations", description: "View reorder recommendations", category: "ai" },
  { key: "ai.reorder.approve", label: "Approve Reorder Recommendations", description: "Approve reorder recommendations", category: "ai" },
  
  // Pricing
  { key: "pricing.matrix.view", label: "View Price Matrix", description: "View price matrix", category: "pricing" },
  { key: "pricing.matrix.edit", label: "Edit Price Matrix", description: "Edit price matrix", category: "pricing" },
  { key: "pricing.margin-analysis.view", label: "View Margin Analysis", description: "View margin analysis", category: "pricing" },
  { key: "pricing.cost-analysis.view", label: "View Cost Analysis", description: "View cost analysis", category: "pricing" },
  
  // Settings
  { key: "settings.tax-rates.view", label: "View Tax Rates", description: "View tax rate configuration", category: "settings" },
  { key: "settings.tax-rates.create", label: "Create Tax Rates", description: "Create new tax rates", category: "settings" },
  { key: "settings.tax-rates.edit", label: "Edit Tax Rates", description: "Edit tax rates", category: "settings" },
  { key: "settings.tax-rates.delete", label: "Delete Tax Rates", description: "Delete tax rates", category: "settings" },
  { key: "settings.custom-fields.view", label: "View Custom Fields", description: "View custom field definitions", category: "settings" },
  { key: "settings.custom-fields.create", label: "Create Custom Fields", description: "Create new custom fields", category: "settings" },
  { key: "settings.custom-fields.edit", label: "Edit Custom Fields", description: "Edit custom fields", category: "settings" },
  { key: "settings.custom-fields.delete", label: "Delete Custom Fields", description: "Delete custom fields", category: "settings" },
  { key: "settings.integration.view", label: "View Integration Setup", description: "View integration settings", category: "settings" },
  { key: "settings.integration.edit", label: "Edit Integration Setup", description: "Edit integration settings", category: "settings" },
  { key: "settings.sync-status.view", label: "View Sync Status", description: "View sync status", category: "settings" },
  { key: "settings.stock-options.view", label: "View Stock Options", description: "View stock options", category: "settings" },
  { key: "settings.stock-options.edit", label: "Edit Stock Options", description: "Edit stock options", category: "settings" },
  { key: "settings.company-details.view", label: "View Company Details", description: "View company information", category: "settings" },
  { key: "settings.user-profiles.view", label: "View User Profiles", description: "View user profiles", category: "settings" },
  { key: "settings.user-profiles.create", label: "Create User Profiles", description: "Create new user profiles", category: "settings" },
  { key: "settings.user-profiles.edit", label: "Edit User Profiles", description: "Edit user profiles", category: "settings" },
  { key: "settings.user-profiles.delete", label: "Delete User Profiles", description: "Delete user profiles", category: "settings" },
  { key: "settings.users.view", label: "View Users", description: "View users list", category: "settings" },
  { key: "settings.users.create", label: "Create Users", description: "Create new users", category: "settings" },
  { key: "settings.users.edit", label: "Edit Users", description: "Edit users", category: "settings" },
  { key: "settings.users.delete", label: "Delete Users", description: "Delete users", category: "settings" },
];
